import { useCallback, useEffect, useState } from "react";
import { api, ApiHata } from "./api";
import { useOturumSurum } from "./oturum";
import { bicimDili, cevir } from "@/lib/dil";

/**
 * Basit veri kancası: GET + yeniden yükleme. Ekranlar "işlem sonrası sunucudan taze durum" desenini kullanır;
 * sunucu her işlemden sonra ekranın tam durumunu döner, istemci onu yerine koyar.
 */
export function useVeri<T>(yol: string | null) {
  // Kimlik değiştiğinde (demo kimlik menüsü) sürüm artar; ekran verisi eski kullanıcıda kalmasın.
  const surum = useOturumSurum();
  const [veri, setVeri] = useState<T | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(Boolean(yol));

  const yenile = useCallback(async () => {
    if (!yol) return;
    try {
      setVeri(await api.get<T>(yol));
      setHata(null);
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Failed to load"));
    } finally {
      setYukleniyor(false);
    }
  }, [yol, surum]);

  useEffect(() => {
    setYukleniyor(true);
    void yenile();
  }, [yenile]);

  return { veri, setVeri, hata, yukleniyor, yenile };
}

/**
 * Yükleme hatası: sunucunun gövdesini de taşır (ör. MRB toplu yüklemede `{hata, hataKaynagi}`, D-060).
 * `ApiHata`dır; mesajı ve durumu eskisi gibi okuyan yakalayıcılar aynen çalışır.
 */
export class YuklemeHatasi extends ApiHata {
  constructor(message: string, status: number, readonly govde: unknown) {
    super(message, status);
  }
}

/**
 * Dosya yükleme: form-data, cookie oturumu. Ek metin alanları `alanlar`,
 * ek dosyalar `ekDosyalar` ile (ör. MRB intake'te PDF'in yanındaki içerik excel'i).
 */
/**
 * `dosyaAlan`: birincil dosyanin form-data alan adi. Varsayilan "dosya"; ucun bekledigi ad
 * farkliysa (or. rework gecis excel'i "gecis") burada verilir. Eskiden sabit "dosya" idi ve
 * ek dosyalardan biri de "dosya" adini tasidiginda multer "Unexpected field - dosya" atiyordu.
 */
export async function dosyaYukle<T>(yol: string, dosya: File, alanlar: Record<string, string> = {}, ekDosyalar: Record<string, File> = {}, dosyaAlan = "dosya"): Promise<T> {
  const fd = new FormData();
  fd.append(dosyaAlan, dosya);
  for (const [k, v] of Object.entries(alanlar)) fd.append(k, v);
  for (const [k, f] of Object.entries(ekDosyalar)) fd.append(k, f);
  const r = await fetch(`/api${yol}`, { method: "POST", body: fd, credentials: "same-origin" });
  const govde = await r.json().catch(() => null);
  if (!r.ok) {
    // Sunucu metni `lib/api.ts`'teki gibi gösterim anında çevrilir.
    const g = govde as { hata?: string; message?: string } | null;
    const mesaj = g?.hata ?? g?.message;
    throw new YuklemeHatasi(mesaj ? cevir(mesaj) : cevir("Upload failed"), r.status, govde);
  }
  return govde as T;
}

/** Tarayıcıdan dosya seçtirir; iptalde null. */
export function dosyaSec(accept = "*/*", capture?: "environment"): Promise<File | null> {
  return new Promise((resolve) => {
    const i = document.createElement("input");
    i.type = "file";
    i.accept = accept;
    if (capture) i.setAttribute("capture", capture);
    // Sayfaya bağlı gizli input: bazı mobil tarayıcılar kameradan dönünce ayrık input'ta change olayını atlar
    i.style.display = "none";
    document.body.append(i);
    i.onchange = () => { resolve(i.files?.[0] ?? null); i.remove(); };
    i.oncancel = () => { resolve(null); i.remove(); };
    i.click();
  });
}

export const tarih = (v: string | Date | null | undefined) => (v ? new Date(v).toLocaleDateString(bicimDili()) : "—");
export const tarihSaat = (v: string | Date | null | undefined) => (v ? new Date(v).toLocaleString(bicimDili(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
